import { useAuthStore } from "@/features/auth/auth.store";
import { BASE_URL } from "@/shared/consts/api";
import { authClient } from "@/shared/lib/axios";
import {
	type AlphaChatContinueRequest,
	AlphaChatContinueRequestSchema,
	type AlphaChatStartRequest,
	AlphaChatStartRequestSchema,
	type AutomationChatHistoryResponse,
	AutomationChatHistoryResponseSchema,
	type AutomationChatLogRequest,
	AutomationChatLogRequestSchema,
	type AutomationChatLogResponse,
	AutomationChatLogResponseSchema,
	type SSEEvent,
	SSEEventSchema,
} from "./agent.types";

const AGENT_ENDPOINT = "/agent";

export const agentApi = {
	alphaChatStart: (
		request: AlphaChatStartRequest,
		onEvent: (event: SSEEvent) => void,
		onError?: (error: Error) => void,
	): AbortController => {
		const payload = AlphaChatStartRequestSchema.parse(request);
		const controller = new AbortController();
		const token = useAuthStore.getState().accessToken;

		fetch(`${BASE_URL}${AGENT_ENDPOINT}/alpha-chat/start`, {
			method: "POST",
			headers: {
				"Content-Type": "application/json",
				Authorization: `Bearer ${token}`,
				Accept: "text/event-stream",
			},
			body: JSON.stringify(payload),
			signal: controller.signal,
		})
			.then(async (response) => {
				if (!response.ok) {
					throw new Error(`HTTP ${response.status}: ${response.statusText}`);
				}

				const reader = response.body?.getReader();
				if (!reader) throw new Error("No response body");

				const decoder = new TextDecoder();
				let buffer = "";

				while (true) {
					const { done, value } = await reader.read();
					if (done) break;

					buffer += decoder.decode(value, { stream: true });
					const lines = buffer.split("\n");
					buffer = lines.pop() || "";

					for (const line of lines) {
						if (line.startsWith("data: ")) {
							try {
								const data = JSON.parse(line.slice(6));
								const event = SSEEventSchema.parse(data);
								onEvent(event);
							} catch (e) {
								console.error("Failed to parse SSE event:", e);
							}
						}
					}
				}
			})
			.catch((error) => {
				if (error.name !== "AbortError") {
					onError?.(error);
				}
			});

		return controller;
	},

	alphaChatContinue: (
		request: AlphaChatContinueRequest,
		onEvent: (event: SSEEvent) => void,
		onError?: (error: Error) => void,
	): AbortController => {
		const payload = AlphaChatContinueRequestSchema.parse(request);
		const controller = new AbortController();
		const token = useAuthStore.getState().accessToken;

		fetch(`${BASE_URL}${AGENT_ENDPOINT}/alpha-chat/continue`, {
			method: "POST",
			headers: {
				"Content-Type": "application/json",
				Authorization: `Bearer ${token}`,
				Accept: "text/event-stream",
			},
			body: JSON.stringify(payload),
			signal: controller.signal,
		})
			.then(async (response) => {
				if (!response.ok) {
					throw new Error(`HTTP ${response.status}: ${response.statusText}`);
				}

				const reader = response.body?.getReader();
				if (!reader) throw new Error("No response body");

				const decoder = new TextDecoder();
				let buffer = "";

				while (true) {
					const { done, value } = await reader.read();
					if (done) break;

					buffer += decoder.decode(value, { stream: true });
					const lines = buffer.split("\n");
					buffer = lines.pop() || "";

					for (const line of lines) {
						if (line.startsWith("data: ")) {
							try {
								const data = JSON.parse(line.slice(6));
								const event = SSEEventSchema.parse(data);
								onEvent(event);
							} catch (e) {
								console.error("Failed to parse SSE event:", e);
							}
						}
					}
				}
			})
			.catch((error) => {
				if (error.name !== "AbortError") {
					onError?.(error);
				}
			});

		return controller;
	},

	automationChatLog: async (
		request: AutomationChatLogRequest,
	): Promise<AutomationChatLogResponse> => {
		const payload = AutomationChatLogRequestSchema.parse(request);
		const { data } = await authClient.post(`${AGENT_ENDPOINT}/automation-chat/log`, payload);
		return AutomationChatLogResponseSchema.parse(data);
	},

	automationChatHistory: async (
		workflowId?: string,
		limit?: number,
	): Promise<AutomationChatHistoryResponse> => {
		const params: Record<string, string> = {};
		if (workflowId) params.workflowId = workflowId;
		if (limit) params.limit = limit.toString();

		const { data } = await authClient.get(`${AGENT_ENDPOINT}/automation-chat/history`, {
			params,
		});
		return AutomationChatHistoryResponseSchema.parse(data);
	},
};
